<template>
    <div>
        <!-- Header -->
        <div
            class="faq-header w-full mx-auto px-4 py-16 md:px-16"
            :style="heroGradientStyle"
        >
            <div class="container">
                <div class="flex flex-col items-center justify-center gap-5">
                    <h1
                        class="text-center text-white font-bold text-36 leading-11 md:text-32 lg:leading-normal"
                    >
                        {{ pageTitle }} FAQs
                    </h1>

                    <p class="text-white text-lg text-center mb-1">
                        How can we help?
                    </p>

                    <SearchInput v-model="searchKeyword" />
                </div>
            </div>
        </div>

        <!-- Content -->
        <div
            class="faq-content container flex flex-col md:flex-row items-start gap-6 md:gap-8 pt-4 lg:pt-20 pb-16"
        >
            <!-- Left Side: Table of Contents -->
            <aside class="faq-sidebar w-full md:w-1/4">
                <SideBar
                    :search-keyword="searchKeyword"
                    :active-color="activeColor"
                    @category-changed="handleCategoryChange"
                />
            </aside>

            <!-- Right Side: FAQ Content -->
            <main class="w-full md:w-3/4">
                <FAQQuestionList
                    :questions="allQuestions"
                    :has-more="hasMoreQuestions"
                    :loading="isLoadingCategory"
                    :active-color="activeColor"
                    @load-more="handleLoadMore"
                />
            </main>
        </div>
    </div>
</template>

<script>
import SearchInput from './components/SearchInput.vue';
import SideBar from './components/SideBar.vue';
import FAQQuestionList from './components/FAQQuestionList.vue';

export default {
    name: 'FaqIndex',

    components: {
        SearchInput,
        SideBar,
        FAQQuestionList,
    },

    props: {
        heroGradientStart: { type: String, default: '#1f4484' },
        heroGradientEnd: { type: String, default: '#0a7ecc' },
        activeColor: { type: String, default: '#b32026' },
        pageTitle: { type: String, default: 'CDL Job Now' },
    },

    data() {
        return {
            allQuestions: [],
            isLoadingCategory: false,
            currentCategory: null,
            paginationMeta: null,
            currentPage: 1,
            searchKeyword: '',
            searchDebounceTimer: null,
        };
    },

    computed: {
        hasMoreQuestions() {
            if (this.paginationMeta) {
                return (
                    this.paginationMeta.current_page <
                    this.paginationMeta.last_page
                );
            }
        },
        heroGradientStyle() {
            return {
                background: `linear-gradient(to right, ${this.heroGradientStart}, ${this.heroGradientEnd})`,
            };
        },
    },

    watch: {
        currentCategory: {
            async handler() {
                // Load questions when category changes (even if null, to show all when searching)
                this.isLoadingCategory = true;
                try {
                    await this.getQuestions();
                } finally {
                    this.isLoadingCategory = false;
                }
            },
            immediate: true,
        },
        searchKeyword(newValue) {
            // Clear existing timer
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }

            // Set new timer
            this.searchDebounceTimer = setTimeout(async () => {
                // Reset to first page when searching
                this.currentPage = 1;

                this.isLoadingCategory = true;
                try {
                    await this.getQuestions();
                } finally {
                    this.isLoadingCategory = false;
                }
            }, 500); // 500ms debounce
        },
    },

    methods: {
        handleCategoryChange(categoryId) {
            this.currentCategory = categoryId;
            this.currentPage = 1;
        },

        async getQuestions(page = 1) {
            let url = `/api/faq/questions?paginate=10&page=${page}`;

            if (this.currentCategory) {
                url += `&category_id=${this.currentCategory}`;
            }

            if (this.searchKeyword) {
                url += `&search=${encodeURIComponent(this.searchKeyword)}`;
            }

            const { data } = await axios.get(url);

            if (data.pagination) {
                // Paginated response
                if (page === 1) {
                    this.allQuestions = data.questions;
                } else {
                    // Append to existing questions for load more
                    this.allQuestions = [
                        ...this.allQuestions,
                        ...data.questions,
                    ];
                }
                this.paginationMeta = data.pagination;
            }
        },

        async handleLoadMore(doneCallback) {
            if (
                this.paginationMeta &&
                this.paginationMeta.current_page < this.paginationMeta.last_page
            ) {
                this.currentPage += 1;
                await this.getQuestions(this.currentPage);

                if (doneCallback && typeof doneCallback === 'function') {
                    doneCallback();
                }
            }
        },
    },
};
</script>

<style scoped>
.faq-header {
    min-height: 302px;
}

.faq-sidebar {
    align-self: flex-start;
}

@media (min-width: 768px) {
    .faq-sidebar {
        position: sticky;
        top: 4rem;
        max-height: calc(100vh - 4rem);
        overflow-y: auto;
    }
}
</style>
